import { Box, VBox } from "@ui/box/Box";
import { Typo } from "@ui/text/Typo";
import { useCurrentPath, useRouter } from "src/services/Router";
import { IMenu, useMenu } from "src/Sitemap";
import { GD } from "../GD";
import { MOBILE_VIEWPORT_MEDIA as MOBILE } from "@ui/utils/mobileViewport";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import Panel from "@ui/panel/Panel";
import { registerSystemBackOverlay } from "@ui/utils/systemBack";

const MENU_TEXT = "#C1CADB";
const MENU_MUTED = "#A0AEC4";
const MENU_BORDER = "#293349";
const MENU_SOFT = "#202B42";
const NAV_ACTIVE = "#1F3D5D";
const NAV_IDLE = "#7A8699";

export const LeftMenu = () => {
  const menu = useMenu();
  const current = useCurrentPath();
  const [moreOpen, setMoreOpen] = useState(false);
  const overflow = menu.length > 5 ? menu.filter(item => item.id === "teams" || item.id === "referral") : [];
  useEffect(() => {
    if (moreOpen) return registerSystemBackOverlay(() => { setMoreOpen(false); return true; });
  }, [moreOpen]);

  return (
    <Box
      role="navigation"
      aria-label="Workspace navigation"
      sx={{
        overflow: "hidden",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        [MOBILE]: {
          height: "auto",
          flexDirection: "row",
          width: "100%",
        },
      }}
    >
      <Project />

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          overflow: "auto",
          flex: 1,
          gap: "6px",
          alignItems: "normal",
          marginTop: "12px",
          [MOBILE]: {
            flexDirection: "row",
            marginTop: "0",
            overflowX: "hidden",
            overflowY: "hidden",
            gap: "2px",
            width: "100%",
          },
        }}
      >
        {menu?.map((val, i) => {
          return <MenuItem data={val} key={i} mobileHidden={overflow.some(item => item.id === val.id)} />;
        })}
        {overflow.length > 0 && <Box sx={{ display: "none", [MOBILE]: { display: "flex", flex: "1 1 0", minWidth: 0 } }}>
          <MenuItem data={{ id: "more", type: "item", label: "More", icon: <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><circle cx="5" cy="12" r="2"/><circle cx="12" cy="12" r="2"/><circle cx="19" cy="12" r="2"/></svg> }} selectedOverride={moreOpen || overflow.some(item => item.id === current?.currentNav?.id)} onSelect={() => setMoreOpen(true)} />
        </Box>}
      </Box>
      {moreOpen && createPortal(<Panel decorator="absolute" label="More sections" growContent onClose={() => setMoreOpen(false)} sx={{ maxWidth: "480px" }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          {overflow.map(item => <MenuItem key={item.id} data={item} mobileRow onSelect={() => setMoreOpen(false)} />)}
        </Box>
      </Panel>, document.body)}
    </Box>
  );
};

const MenuItem = ({ data, mobileHidden = false, mobileRow = false, onSelect, selectedOverride }: { data: IMenu; mobileHidden?: boolean; mobileRow?: boolean; onSelect?: () => void; selectedOverride?: boolean }) => {
  const router = useRouter();
  const current = useCurrentPath();

  if (data.type === "ignore") return <></>;

  const selected = selectedOverride ?? current?.currentNav?.id === data.id;
  const title = data.label;

  //todo check if profile is merchant and only one merchant

  if (data.type === "divider")
    return (
      <Box
        sx={{
          height: "1px",
          margin: "10px 6px",
          backgroundColor: MENU_BORDER,
        }}
      />
    );

  if (data.type === "label") {
    return (
      <Typo
        sx={{
          color: MENU_MUTED,
          fontSize: "12px",
          fontWeight: 700,
          padding: "8px 12px 4px",
          userSelect: "none",
        }}
      >
        {data.label}
      </Typo>
    );
  }

  return (
    <Box
      element="button"
      type="button"
      aria-label={title}
      aria-current={selected ? "page" : undefined}
      data-nav-item
      sx={{
        cursor: "pointer",
        backgroundColor: selected ? "#2B5C8A" : "transparent",
        display: "flex",
        gap: "10px",
        padding: "11px 12px",
        fontFamily: "inherit",
        textAlign: "left",
        width: "100%",
        transition:
          "background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease, transform 0.12s ease",
        alignItems: "center",
        borderRadius: "12px",
        border: "0",
        [MOBILE]: {
          display: mobileHidden ? "none" : "flex",
          backgroundColor: "transparent",
          borderColor: "transparent",
          color: selected ? NAV_ACTIVE : NAV_IDLE,
          padding: mobileRow ? "10px 12px" : "5px 0 4px",
          gap: mobileRow ? "12px" : "3px",
          flex: "1 1 0",
          minWidth: 0,
          minHeight: mobileRow ? "56px" : "54px",
          flexDirection: mobileRow ? "row" : "column",
          justifyContent: mobileRow ? "flex-start" : "center",
          borderRadius: "16px",
          WebkitTapHighlightColor: "transparent",
        },
        "&:active": { transform: "scale(0.96)" },
        "&:focus-visible": { outline: `2px solid ${NAV_ACTIVE}`, outlineOffset: "-2px" },
        color: selected ? "#FFFFFF" : MENU_MUTED,
      }}
      onClick={() => {
        if (data.id !== "more") router.navigate(data.id);
        onSelect?.();
      }}
    >
      <Box
        sx={{
          width: "34px",
          height: "34px",
          borderRadius: "10px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flex: "0 0 34px",
          lineHeight: "0px",
          color: "inherit",
          backgroundColor: selected ? "rgba(255,255,255,.12)" : "transparent",
          border: "0",
          [MOBILE]: {
            width: "46px",
            height: "30px",
            flexBasis: mobileRow ? "46px" : "30px",
            borderRadius: "999px",
            backgroundColor: selected ? NAV_ACTIVE : "transparent",
            color: selected ? "#FFFFFF" : "inherit",
            boxShadow: selected ? "0 4px 12px rgba(31, 61, 93, 0.32)" : "none",
            transition: "background-color 0.2s ease, box-shadow 0.2s ease",
          },
          "& svg": { width: "22px", height: "22px" },
        }}
      >
        {data.icon}
      </Box>
      <Typo
        sx={{
          color: selected ? "#FFFFFF" : MENU_TEXT,
          fontSize: "15px",
          userSelect: "none",
          fontWeight: selected ? 700 : 600,
          [MOBILE]: {
            color: selected ? NAV_ACTIVE : NAV_IDLE,
            fontWeight: selected ? 700 : 600,
            display: "block",
            width: "100%",
            minWidth: 0,
            fontSize: mobileRow ? "15px" : "11px",
            letterSpacing: mobileRow ? undefined : "0.01em",
            lineHeight: 1.35,
            textAlign: mobileRow ? "left" : "center",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          },
        }}
      >
        <Box element="span" sx={{ [MOBILE]: { display: title === "Transactions" || title === "Dashboard" ? "none" : "inline" } }}>{title}</Box>
        {(title === "Transactions" || title === "Dashboard") && <Box element="span" sx={{ display: "none", [MOBILE]: { display: "inline" } }}>{title === "Dashboard" ? "Home" : "Activity"}</Box>}
      </Typo>
    </Box>
  );
};

const Project = () => {
  const [project] = GD.REQ_CURRENT_PROJECT.useRequest();

  if (!project) return null;

  return (
    <VBox
      sx={{
        padding: "14px",
        backgroundColor: "#202B42",
        border: `1px solid ${MENU_BORDER}`,
        borderRadius: "12px",
        cursor: "pointer",
        transition: "background-color 0.2s ease, border-color 0.2s ease",
        [MOBILE]: { display: "none" },
        "&:hover": {
          backgroundColor: MENU_SOFT,
          borderColor: "#E5E7EB",
        },
      }}
      onClick={() => {
        location.href = "/";
        //GD.S_SELECT_PROJECT.invoke();
      }}
    >
      <Typo
        variant="small"
        sx={{ color: MENU_MUTED, fontSize: "12px", fontWeight: 700 }}
      >
        Company
      </Typo>
      <Typo
        variant="headMedium"
        sx={{
          color: MENU_TEXT,
          fontSize: "18px",
          fontWeight: 800,
          lineHeight: "1.25",
        }}
      >
        {project?.name}
      </Typo>
    </VBox>
  );
};
